import { Component, OnInit, OnDestroy, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { PropertyService, Property, PropertyImage } from '../../services/property.service';

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './home.component.html',
  styleUrl: './home.component.css'
})
export class HomeComponent implements OnInit, OnDestroy {
  properties = signal<Property[]>([]);
  loading = signal(true);
  error = signal('');
  propertyImageIndices: { [key: number]: number } = {}; // Track current image index for each property
  
  // Carousel state
  currentCarouselIndex = signal(0);
  private carouselInterval: any = null;

  constructor(public propertyService: PropertyService, private router: Router) {}

  ngOnInit() {
    this.loadHotProperties();
  }

  ngOnDestroy() {
    this.stopCarousel();
  }

  private startCarousel() {
    this.stopCarousel();
    this.carouselInterval = setInterval(() => {
      this.nextCarouselSlide();
    }, 5000); // Change slide every 5 seconds
  }

  private stopCarousel() {
    if (this.carouselInterval) {
      clearInterval(this.carouselInterval);
      this.carouselInterval = null;
    }
  }

  nextCarouselSlide() {
    const props = this.properties();
    if (props.length > 0) {
      this.currentCarouselIndex.set((this.currentCarouselIndex() + 1) % Math.min(props.length, 5));
    }
  }

  previousCarouselSlide() {
    const props = this.properties();
    if (props.length > 0) {
      const max = Math.min(props.length, 5);
      this.currentCarouselIndex.set((this.currentCarouselIndex() - 1 + max) % max);
    }
  }

  goToCarouselSlide(index: number) {
    this.currentCarouselIndex.set(index);
    this.stopCarousel();
    // Restart auto-play after manual navigation
    setTimeout(() => this.startCarousel(), 1000);
  }

  getCarouselProperties(): Property[] {
    return this.properties().slice(0, 5); // Show max 5 in carousel
  }

  private async loadHotProperties() {
    try {
      this.loading.set(true);
      this.error.set('');

      // Load properties marked for homepage display
      const data = await this.propertyService.loadHomepageProperties();
      
      // Ensure property_images are properly loaded
      const propertiesWithImages = data.map(prop => ({
        ...prop,
        property_images: prop.property_images || []
      }));
      
      this.properties.set(propertiesWithImages);
      // Pre-load all image URLs
      await this.propertyService.preloadImageUrls(propertiesWithImages);
      // Start carousel auto-play
      if (propertiesWithImages.length > 0) {
        this.startCarousel();
      }
    } catch (err: any) {
      this.error.set(err.message || 'Failed to load properties');
      console.error('Error:', err);
    } finally {
      this.loading.set(false);
    }
  }

  get filteredProperties(): Property[] {
    return this.properties();
  }

  getPropertyImages(property: Property): PropertyImage[] {
    return property.property_images || [];
  }

  getImageUrlFromCache(storagePath: string): string {
    return this.propertyService.getImageUrlFromCache(storagePath);
  }

  viewPropertyDetail(propertyId: string) {
    this.router.navigate(['/property', propertyId]);
  }

  getPropertyImageUrl(property: Property, propertyIdx: number): string {
    const images = this.getPropertyImages(property);
    if (!images || images.length === 0) return '';
    
    // Initialize to primary image index if not set
    if (this.propertyImageIndices[propertyIdx] === undefined) {
      const primaryImage = this.propertyService.getPrimaryImage(property);
      if (primaryImage) {
        const primaryIndex = images.findIndex(img => img.id === primaryImage.id);
        this.propertyImageIndices[propertyIdx] = primaryIndex >= 0 ? primaryIndex : 0;
      } else {
        this.propertyImageIndices[propertyIdx] = 0;
      }
    }
    
    const currentIndex = this.propertyImageIndices[propertyIdx];
    const image = images[currentIndex];
    
    if (!image || !image.storage_path) return '';
    
    const url = this.getImageUrlFromCache(image.storage_path);
    return url;
  }

  nextPropertyImage(propertyIdx: number) {
    const images = this.getPropertyImages(this.properties()[propertyIdx]);
    const currentIndex = this.propertyImageIndices[propertyIdx] || 0;
    this.propertyImageIndices[propertyIdx] = (currentIndex + 1) % images.length;
  }

  previousPropertyImage(propertyIdx: number) {
    const images = this.getPropertyImages(this.properties()[propertyIdx]);
    const currentIndex = this.propertyImageIndices[propertyIdx] || 0;
    this.propertyImageIndices[propertyIdx] = (currentIndex - 1 + images.length) % images.length;
  }
}
